import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, TrendingUp, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { API } from "../config/api";

const SentimentCard = ({ ticker }) => {
  const [sentiment, setSentiment] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSentiment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/stock/sentiment`, { ticker });
      setSentiment(response.data);
    } catch (error) {
      console.error("Error fetching sentiment:", error);
      toast.error("Failed to analyze sentiment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSentiment(null);
  }, [ticker]);

  return (
    <Card className="border-slate-200 bg-white/80 backdrop-blur-sm" data-testid="sentiment-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-600" />
          AI Sentiment Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!sentiment ? (
          <div className="text-center py-8">
            <p className="text-slate-600 mb-4">Get AI-powered sentiment analysis based on latest news and market data</p>
            <Button 
              onClick={fetchSentiment} 
              disabled={loading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              data-testid="analyze-sentiment-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Analyze Sentiment
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <span className="text-sm text-slate-600">Based on {sentiment.newsCount} news articles</span>
              </div>
              <Button 
                onClick={fetchSentiment} 
                variant="ghost" 
                size="sm"
                disabled={loading}
                data-testid="refresh-sentiment-button"
              >
                Refresh
              </Button>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="sentiment-analysis">
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-slate-700">{sentiment.analysis}</pre>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">AI analysis is for informational purposes only. Always do your own research before making investment decisions.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SentimentCard;